import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCallback, useMemo } from 'react'
import Card from 'app/frontend/base/components/Card'
import RichTextContent from 'app/frontend/base/components/RichTextContent'
import { hasNotesContent } from 'app/frontend/base/components/NotesEditor'
import { Separator } from '@/components/ui/separator'
import { getClass } from 'app/frontend/base/services/characterValues'
import { getTimelineSpellNameById } from 'app/frontend/base/services/raidPlanData'
import { getTimelineSpellsById } from 'app/frontend/base/services/raidPlanData'
import {
  ActorTimelineRow,
  sortActorTimelineRows,
} from '../../TeamPlans/components/ActorTimelineContainer'
import { EventActivityLinkedPlanData } from '../hooks/useEventActivityLinkedPlanData'
import EventActivityPlanAbilityRow from './EventActivityPlanAbilityRow'
import ReadonlyCooldownTimeline from '../../TeamPlans/components/ReadonlyCooldownTimeline'
import { resolveLinkedPlanTimelineMaxTime } from '../hooks/resolveLinkedPlanEncounterAssignmentRows'

type Props = {
  activity: wowaudit.EventActivity
  open: boolean
  onOpenChange: (open: boolean) => void
  linkedPlanData?: EventActivityLinkedPlanData
  assignedAbilityKeys: string[]
  characterId: number
}

const ViewerParticipantAssignmentAbilitiesDialog = ({
  activity,
  open,
  onOpenChange,
  linkedPlanData,
  assignedAbilityKeys,
  characterId,
}: Props) => {
  const encounterLabel =
    activity.encounter?.name ??
    activity.instance?.name ??
    'Activity assignments'
  const assignedAbilityKeysSet = new Set(assignedAbilityKeys)
  const linkedPlanAssignments = linkedPlanData?.linkedPlanAssignments ?? []
  const selectedAbilityRows = linkedPlanAssignments
    .filter((entry) => assignedAbilityKeysSet.has(entry.row.abilityKey))
    .map((entry) => ({
      row: entry.row,
      ability: entry.ability,
      abilityKey: entry.abilityKey,
    }))
  const spellById = useMemo(() => getTimelineSpellsById(), [])
  const classColor = useCallback((classId?: number) => {
    if (classId === undefined || classId === null) return 'var(--color-primary)'
    const className = getClass(classId.toString())
    if (!className) return 'var(--color-primary)'
    return `var(--color-class-${className.toLowerCase().replace(/\s+/g, '-')})`
  }, [])
  const spellNameById = useMemo(() => getTimelineSpellNameById(), [])
  const participantCooldownRows = useMemo(() => {
    const matchedActors = (linkedPlanData?.resolvedActors ?? []).filter(
      (resolvedActor) => resolvedActor.participant?.characterId === characterId,
    )
    const rowsBySpellId: Record<string, ActorTimelineRow> = {}
    const upsertRow = (spellId: number | null) => {
      const key = spellId === null ? 'spell:null' : `spell:${spellId}`
      if (!rowsBySpellId[key]) {
        rowsBySpellId[key] = {
          key: `participant-cooldown-${key}`,
          spellId,
          casts: [],
        }
      }
      return rowsBySpellId[key]
    }
    matchedActors.forEach((resolvedActor) => {
      resolvedActor.actor.casts.forEach((cast) => {
        const spellId = cast.spell_id
        const row = upsertRow(spellId)
        const spell = spellId === null ? undefined : spellById[spellId]
        row.casts.push({
          id: cast.id,
          spellId,
          at: cast.at,
          duration: spell?.duration ?? 0,
          cooldown: spell?.cooldown ?? 0,
          color: classColor(resolvedActor.actor.class_id),
          issueType: 'none',
          message: cast.message,
          ttsMessage: cast.tts_message,
          rgb: cast.rgb,
          targets: cast.targets,
        })
      })
    })
    ;(linkedPlanData?.linkedRaidPlan?.timeline.general?.casts ?? []).forEach(
      (cast) => {
        const spellId = cast.spell_id
        const row = upsertRow(spellId)
        const spell = spellId === null ? undefined : spellById[spellId]
        row.casts.push({
          id: `general-${cast.id}`,
          spellId,
          at: cast.at,
          duration: spell?.duration ?? 0,
          cooldown: spell?.cooldown ?? 0,
          color: 'var(--color-primary)',
          issueType: 'none',
          message: cast.message,
          ttsMessage: cast.tts_message,
          rgb: cast.rgb,
          targets: cast.targets,
        })
      },
    )
    return sortActorTimelineRows(
      Object.values(rowsBySpellId),
      (spellId) =>
        spellId === null
          ? 'Message'
          : spellNameById[spellId] ?? `Spell ${spellId}`,
      (spellId) => (spellId === null ? 0 : spellById[spellId]?.cooldown ?? 0),
    )
  }, [
    characterId,
    linkedPlanData?.linkedRaidPlan?.timeline.general?.casts,
    linkedPlanData?.resolvedActors,
    classColor,
    spellById,
    spellNameById,
  ])
  const participantCooldownMaxTime = useMemo(() => {
    if (linkedPlanData?.linkedRaidPlan) {
      return resolveLinkedPlanTimelineMaxTime({
        raidPlan: linkedPlanData.linkedRaidPlan,
        timeline: linkedPlanData.linkedRaidPlan.timeline,
      })
    }
    return Math.max(
      participantCooldownRows.reduce((maxValue, row) => {
        const rowMax = row.casts.reduce((castMax, cast) => {
          return Math.max(
            castMax,
            cast.at + Math.max(cast.cooldown, cast.duration, 0),
          )
        }, 0)
        return Math.max(maxValue, rowMax)
      }, 0),
      1,
    )
  }, [linkedPlanData?.linkedRaidPlan, participantCooldownRows])
  const hasAssignments = selectedAbilityRows.length > 0
  const hasCooldowns = participantCooldownRows.length > 0
  const hasActivityNotes = hasNotesContent(activity.notes)
  const hasPlanMessage = hasNotesContent(
    linkedPlanData?.linkedRaidPlan?.message,
  )
  const hasNotes = hasActivityNotes || hasPlanMessage

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='max-w-7xl w-fit'
        onOpenAutoFocus={(event) => {
          event.preventDefault()
        }}
      >
        <DialogHeader>
          <DialogTitle>{encounterLabel}</DialogTitle>
        </DialogHeader>
        {!hasAssignments && !hasCooldowns ? (
          <div className='text-sm text-muted-foreground'>
            You don&apos;t have any assignments for this encounter.
          </div>
        ) : (
          <div className='max-h-[95vh] overflow-y-auto pr-1'>
            {hasNotes ? (
              <div className='flex flex-col gap-1 mb-4'>
                <span className='text-sm text-muted-foreground'>Message</span>
                <Card>
                  {hasActivityNotes ? (
                    <RichTextContent content={activity.notes ?? ''} />
                  ) : null}
                  {hasActivityNotes && hasPlanMessage ? (
                    <Separator className='my-4' />
                  ) : null}
                  {hasPlanMessage ? (
                    <RichTextContent
                      content={linkedPlanData?.linkedRaidPlan?.message ?? ''}
                    />
                  ) : null}
                </Card>
              </div>
            ) : null}
            {hasAssignments ? (
              <>
                <h4>Assignments</h4>
                {selectedAbilityRows.map(({ row, ability, abilityKey }) => (
                  <EventActivityPlanAbilityRow
                    key={abilityKey}
                    row={row}
                    ability={ability}
                    phaseDividers={linkedPlanData?.phaseDividers ?? []}
                    resolvedByScopeBoxKey={
                      linkedPlanData?.resolverResult.resolvedByScopeBoxKey ?? {}
                    }
                    selectedParticipants={
                      linkedPlanData?.selectedParticipants ?? []
                    }
                    onOverrideSelect={() => undefined}
                    onResetAbilityOverrides={() => undefined}
                    onSetAbilityHidden={() => undefined}
                    isReadonly
                  />
                ))}
              </>
            ) : null}
            <div className={hasAssignments ? 'mt-6' : ''}>
              <h4>Cooldowns</h4>
              {hasCooldowns ? (
                <ReadonlyCooldownTimeline
                  rows={participantCooldownRows}
                  maxTime={participantCooldownMaxTime}
                  spellNameById={spellNameById}
                  timelineMinWidthClass='min-w-[25rem]'
                />
              ) : (
                <div className='text-sm text-muted-foreground'>
                  You don&apos;t have any cooldown casts for this encounter.
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ViewerParticipantAssignmentAbilitiesDialog
